import React, { useEffect, useState } from "react";
import { useRouter } from "next/router";
import { CrossBlackIcon } from "@/src/Asset";
import { updateSpecializations } from "../helper";

interface SpecializationSectionProps {
  readonly filterData: any;
  readonly filterOptions: any;
  readonly filterSecStreamData: any;
  readonly setSelectedFilterCategory: (category: string | null) => void;
  filterSpecialization: any;
}

const getCommonSpecializations = (
  availableSpecializations: any,
  filterSpecialization: any
) => {
  const filterIds = new Set(filterSpecialization?.map((item: any) => item.id));

  const common = availableSpecializations.filter((item: any) =>
    filterIds.has(item.id)
  );

  return common;
};

export default function SpecializationSection({
  filterData,
  filterOptions,
  filterSecStreamData,
  setSelectedFilterCategory,
  filterSpecialization,
}: Readonly<SpecializationSectionProps>) {
  // Add client-side detection for mobile view
  const [isMobile, setIsMobile] = useState(false);
  
  

  const [availableSpecializations, setAvailableSpecializations] = useState<
    any[]
  >([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [filteredSpecializations, setFilteredSpecializations] = useState<any[]>(
    []
  );
  const [noMatchData, setNoMatchData] = useState(false);
  const [isRedirecting, setIsRedirecting] = useState(false);

  const router = useRouter();

  useEffect(() => {
    if (filterOptions.country) {
      const specializations = updateSpecializations(
        filterData,
        filterOptions,
        filterSecStreamData
      );

      // If there's a selected specialization, add it to the list
      if (
        filterOptions?.specialization &&
        !specializations.some((s) => s.id === filterOptions.specialization.id)
      ) {
        specializations.push(filterOptions.specialization);
      }

      setAvailableSpecializations(specializations);
      setFilteredSpecializations(specializations);
    }
  }, [filterOptions, filterSecStreamData, filterData]);

  useEffect(() => {
    if (searchQuery.length >= 3) {
      setFilteredSpecializations(() => {
        const lowerCaseQuery = searchQuery.toLowerCase();

        // First, find specializations that match the query
        const matchingStates = availableSpecializations.filter(
          (specializations: any) => {
            return (
              specializations?.specialization_name
                ?.toLowerCase()
                ?.includes(lowerCaseQuery) ??
              specializations?.specialization_url
                ?.toLowerCase()
                ?.includes(lowerCaseQuery) ??
              specializations?.abbreviation
                ?.toLowerCase()
                ?.includes(lowerCaseQuery)
            );
          }
        );
        if (matchingStates?.length === 0) {
          setNoMatchData(true);
        } else {
          setNoMatchData(false);
        }
        const nonMatchingStates = availableSpecializations.filter(
          (specializations: any) =>
            !specializations?.specialization_name
              ?.toLowerCase()
              ?.includes(lowerCaseQuery)
        );
        // Prioritize matching states first
        return [...matchingStates, ...nonMatchingStates];
      });
    } else {
      setFilteredSpecializations(availableSpecializations); // Reset if searchQuery is less than 3 chars
    }
  }, [searchQuery, availableSpecializations]);

  // Check if we're in a mobile view  
  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth < 1024); // 1024px is the lg breakpoint in Tailwind
    };
    
    // Initial check
    checkIfMobile();
    
    // Listen for resize events
    window.addEventListener('resize', checkIfMobile);
    
    // Cleanup
    return () => window.removeEventListener('resize', checkIfMobile);
  }, []);
  
  // Early returns after all hooks are called
  if (!filterOptions?.stream || !filterOptions?.course) {
    return null;
  }
  
  // Don't render anything on mobile
  if (isMobile) {
    return null;
  }

  const handleRedirect = async (specializationDetail: any) => {
    if (isRedirecting) return;
    setIsRedirecting(true);

    try {
      const courseName = filterOptions?.course?.course_url;
      const streamName = filterOptions?.stream?.stream_name;
      const locInfo =
        filterOptions.city?.city_url ??
        filterOptions.state?.state_url ??
        filterOptions.country?.country_url?.replace("study-in-", "") ??
        "india";

      try {
        // Store the current state in localStorage
        localStorage.setItem("previousCourseUrl", courseName ?? "");
        localStorage.setItem("previousStreamName", streamName ?? "");
        localStorage.setItem(
          "previousStreamUrl",
          filterOptions?.stream?.stream_url ?? ""
        );
        localStorage.setItem(
          "previousCourseName",
          filterOptions?.course?.course_name ?? ""
        );
      } catch (e) {
        console.warn("Failed to save state to localStorage:", e);
      }

      // Always ensure course name is included in the URL when a specialization is selected
      if (!courseName) {
        console.error("Course name is required for specialization URL");
        return;
      }

      let baseUrl = `${courseName}/${specializationDetail.specialization_url}-colleges-in-${locInfo}`;

      baseUrl = baseUrl
        .replace(/\/+/g, "/")
        .replace(/^\/+/, "")
        .replace(/\/+$/, "")
        .replace(/([^/])-([^/])/g, "$1-$2");

      await router.push(`/${baseUrl}`);
      setSelectedFilterCategory(null);
    } catch (error) {
      console.error("Redirect error:", error);
    } finally {
      setIsRedirecting(false);
    }
  };

  const filteredResult = getCommonSpecializations(
    availableSpecializations,
    filterSpecialization
  );

  // Add selected specialization to filtered results if not present
  const finalFilteredResult = filterOptions?.specialization
    ? [
        filterOptions.specialization,
        ...filteredResult.filter(
          (s: { id: any }) => s.id !== filterOptions.specialization.id
        ),
      ]
    : filteredResult;

  // Determine if we should show "No specialization" message
  const shouldShowNoSpecialization =
    !filterOptions?.specialization && finalFilteredResult.length === 0;

  return (
    <div className="absolute left-[22%] mt-3 !z-10">
      <div className="bg-white rounded-lg shadow-lg border w-[30rem] h-[25rem] overflow-y-auto">
        <div className="sticky top-0 bg-white px-4 pt-4 pb-2">
          <div className="flex justify-between items-center mb-2">
            <div className="text-lg font-medium">Specializations</div>
            <button
              onClick={() => setSelectedFilterCategory(null)}
              className="text-gray-600 hover:text-gray-900 text-2xl"
            >
              <CrossBlackIcon className="scale-75" />
            </button>
          </div>

          <div className="mb-3 flex items-center gap-2">
            <input
              type="text"
              placeholder="Search for a specialization..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full px-3 py-1 border rounded outline-gray-300 text-sm"
            />
            <button
              onClick={() => setSearchQuery("")}
              className="px-4 py-1 bg-gray-200 rounded hover:bg-gray-300 text-sm"
            >
              Clear
            </button>
          </div>
        </div>

        {searchQuery.length >= 3 && noMatchData && (
          <p className="text-red-500 text-sm text-center mt-1">
            No Match Found
          </p>
        )}

        {/* Specialization List */}
        <div className="flex flex-wrap gap-x-3 gap-y-4 justify-start px-4 pb-4">
          {searchQuery.length >= 3 ? (
            // Show filtered results when searching
            filteredSpecializations.map((specializationDetail: any) => {
              const isSelected =
                filterOptions?.specialization?.specialization_name ===
                specializationDetail?.specialization_name;

              const lowerCaseName =
                specializationDetail.specialization_name.toLowerCase();
              const lowerCaseQuery = searchQuery.toLowerCase();
              const startIndex = lowerCaseName.indexOf(lowerCaseQuery);

              return (
                <button
                  key={specializationDetail.id}
                  disabled={isRedirecting}
                  onClick={() => handleRedirect(specializationDetail)}
                  className={`px-4 py-0.5 text-[0.8rem] border border-gray-300 bg-gray-50 rounded-full transition-all whitespace-nowrap duration-200 ${
                    isSelected
                      ? "text-primary hover:text-blue-800 hover:bg-gray-100 font-bold"
                      : "hover:border-primary hover:text-primary hover:bg-white"
                  } ${
                    isRedirecting
                      ? "bg-gray-300 cursor-not-allowed"
                      : "hover:bg-gray-100"
                  }`}
                >
                  {startIndex !== -1 ? (
                    <>
                      {specializationDetail.specialization_name.substring(
                        0,
                        startIndex
                      )}
                      <span className="bg-yellow-300 text-black font-semibold">
                        {specializationDetail.specialization_name.substring(
                          startIndex,
                          startIndex + searchQuery.length
                        )}
                      </span>
                      {specializationDetail.specialization_name.substring(
                        startIndex + searchQuery.length
                      )}
                    </>
                  ) : (
                    specializationDetail.specialization_name
                  )}
                </button>
              );
            })
          ) : shouldShowNoSpecialization ? (
            <div className="w-full text-center text-gray-500 mt-8 text-base">
              No specialization for the course
            </div>
          ) : (
            finalFilteredResult.map((specializationDetail: any) => {
              const isSelected =
                filterOptions?.specialization?.specialization_name ===
                specializationDetail?.specialization_name;

              return (
                <button
                  key={specializationDetail?.id}
                  disabled={isRedirecting}
                  onClick={() => handleRedirect(specializationDetail)}
                  className={`px-4 py-0.5 text-[0.8rem] border border-gray-300 bg-gray-50 rounded-full transition-all whitespace-nowrap duration-200 truncate ${
                    isSelected
                      ? "text-primary hover:text-blue-800 hover:bg-gray-100 font-bold"
                      : "hover:border-primary hover:text-primary hover:bg-white"
                  } ${
                    isRedirecting
                      ? "bg-gray-300 cursor-not-allowed"
                      : "hover:bg-gray-100"
                  }`}
                >
                  {specializationDetail?.specialization_name?.slice(0, 50)}
                </button>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
